import React, { useState, useEffect } from 'react';
import { EditorSystem } from '../../systems/editor/EditorSystem';

interface BoundaryControlWindowProps {
  isOpen: boolean;
  onClose: () => void;
  editorSystem: EditorSystem | null;
}

export const BoundaryControlWindow: React.FC<BoundaryControlWindowProps> = ({
  isOpen,
  onClose,
  editorSystem
}) => {
  const [leftBoundary, setLeftBoundary] = useState(0);
  const [rightBoundary, setRightBoundary] = useState(2000);
  const [enabled, setEnabled] = useState(true);

  // Sync with editor system state
  useEffect(() => {
    if (editorSystem && isOpen) {
      const boundaries = editorSystem.getState().boundaries;
      if (boundaries) {
        setLeftBoundary(boundaries.left);
        setRightBoundary(boundaries.right);
        setEnabled(boundaries.enabled);
      }
    }
  }, [editorSystem, isOpen]);

  const handleToggle = () => {
    const newEnabled = !enabled;
    setEnabled(newEnabled);
    editorSystem?.toggleBoundaries(newEnabled);
  };

  const handleLeftChange = (value: string) => {
    const num = parseInt(value) || 0;
    setLeftBoundary(num);
    editorSystem?.setLeftBoundary(num);
  };

  const handleRightChange = (value: string) => {
    const num = parseInt(value) || 0;
    setRightBoundary(num);
    editorSystem?.setRightBoundary(num);
  };

  const handleNudge = (side: 'left' | 'right', amount: number) => {
    if (side === 'left') {
      const newVal = leftBoundary + amount;
      setLeftBoundary(newVal);
      editorSystem?.setLeftBoundary(newVal);
    } else {
      const newVal = rightBoundary + amount;
      setRightBoundary(newVal);
      editorSystem?.setRightBoundary(newVal);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'absolute',
      top: '80px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '400px',
      backgroundColor: '#2c3e50',
      border: '2px solid #e74c3c',
      borderRadius: '8px',
      padding: '20px',
      color: 'white',
      zIndex: 2000,
      boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
      pointerEvents: 'auto' // Ensure window receives clicks
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        paddingBottom: '10px',
        borderBottom: '1px solid #34495e'
      }}>
        <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
          🚧 Map Boundaries
        </h3>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: '#999',
            fontSize: '20px',
            cursor: 'pointer'
          }}
        >
          ×
        </button>
      </div>

      {/* Enable Toggle */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px',
        padding: '12px',
        backgroundColor: '#34495e',
        borderRadius: '6px'
      }}>
        <span style={{ fontWeight: 'bold' }}>Enable Boundaries</span>
        <button
          onClick={handleToggle}
          style={{
            padding: '8px 20px',
            backgroundColor: enabled ? '#27ae60' : '#e74c3c',
            color: 'white',
            border: 'none',
            borderRadius: '20px',
            cursor: 'pointer',
            fontWeight: 'bold',
            transition: 'all 0.2s'
          }}
        >
          {enabled ? 'ON' : 'OFF'}
        </button>
      </div>

      {/* Left Boundary */}
      <div style={{
        marginBottom: '20px',
        padding: '15px',
        backgroundColor: '#34495e',
        borderRadius: '6px',
        border: '2px solid #e74c3c',
        opacity: enabled ? 1 : 0.5
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '10px',
          color: '#ff6666',
          fontWeight: 'bold'
        }}>
          ⬅️ LEFT BOUNDARY
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
          <span style={{ fontSize: '12px', minWidth: '60px' }}>X Position:</span>
          <input
            type="number"
            value={leftBoundary}
            onChange={(e) => handleLeftChange(e.target.value)}
            disabled={!enabled}
            style={{
              flex: 1,
              padding: '8px',
              backgroundColor: '#2c3e50',
              border: '1px solid #e74c3c',
              borderRadius: '4px',
              color: 'white',
              fontSize: '14px',
              fontFamily: 'monospace'
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '5px' }}>
          <button
            onClick={() => handleNudge('left', -100)}
            disabled={!enabled}
            style={{
              flex: 1,
              padding: '6px',
              backgroundColor: '#2c3e50',
              border: '1px solid #555',
              borderRadius: '4px',
              color: 'white',
              cursor: enabled ? 'pointer' : 'not-allowed',
              fontSize: '12px'
            }}
          >
            ← -100
          </button>
          <button
            onClick={() => handleNudge('left', -10)}
            disabled={!enabled}
            style={{
              flex: 1,
              padding: '6px',
              backgroundColor: '#2c3e50',
              border: '1px solid #555',
              borderRadius: '4px',
              color: 'white',
              cursor: enabled ? 'pointer' : 'not-allowed',
              fontSize: '12px'
            }}
          >
            ← -10
          </button>
          <button
            onClick={() => handleNudge('left', 10)}
            disabled={!enabled}
            style={{
              flex: 1,
              padding: '6px',
              backgroundColor: '#2c3e50',
              border: '1px solid #555',
              borderRadius: '4px',
              color: 'white',
              cursor: enabled ? 'pointer' : 'not-allowed',
              fontSize: '12px'
            }}
          >
            +10 →
          </button>
          <button
            onClick={() => handleNudge('left', 100)}
            disabled={!enabled}
            style={{
              flex: 1,
              padding: '6px',
              backgroundColor: '#2c3e50',
              border: '1px solid #555',
              borderRadius: '4px',
              color: 'white',
              cursor: enabled ? 'pointer' : 'not-allowed',
              fontSize: '12px'
            }}
          >
            +100 →
          </button>
        </div>
      </div>

      {/* Right Boundary */}
      <div style={{
        marginBottom: '20px',
        padding: '15px',
        backgroundColor: '#34495e',
        borderRadius: '6px',
        border: '2px solid #e74c3c',
        opacity: enabled ? 1 : 0.5
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '10px',
          color: '#ff6666',
          fontWeight: 'bold'
        }}>
          ➡️ RIGHT BOUNDARY
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
          <span style={{ fontSize: '12px', minWidth: '60px' }}>X Position:</span>
          <input
            type="number"
            value={rightBoundary}
            onChange={(e) => handleRightChange(e.target.value)}
            disabled={!enabled}
            style={{
              flex: 1,
              padding: '8px',
              backgroundColor: '#2c3e50',
              border: '1px solid #e74c3c',
              borderRadius: '4px',
              color: 'white',
              fontSize: '14px',
              fontFamily: 'monospace'
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '5px' }}>
          <button
            onClick={() => handleNudge('right', -100)}
            disabled={!enabled}
            style={{
              flex: 1,
              padding: '6px',
              backgroundColor: '#2c3e50',
              border: '1px solid #555',
              borderRadius: '4px',
              color: 'white',
              cursor: enabled ? 'pointer' : 'not-allowed',
              fontSize: '12px'
            }}
          >
            ← -100
          </button>
          <button
            onClick={() => handleNudge('right', -10)}
            disabled={!enabled}
            style={{
              flex: 1,
              padding: '6px',
              backgroundColor: '#2c3e50',
              border: '1px solid #555',
              borderRadius: '4px',
              color: 'white',
              cursor: enabled ? 'pointer' : 'not-allowed',
              fontSize: '12px'
            }}
          >
            ← -10
          </button>
          <button
            onClick={() => handleNudge('right', 10)}
            disabled={!enabled}
            style={{
              flex: 1,
              padding: '6px',
              backgroundColor: '#2c3e50',
              border: '1px solid #555',
              borderRadius: '4px',
              color: 'white',
              cursor: enabled ? 'pointer' : 'not-allowed',
              fontSize: '12px'
            }}
          >
            +10 →
          </button>
          <button
            onClick={() => handleNudge('right', 100)}
            disabled={!enabled}
            style={{
              flex: 1,
              padding: '6px',
              backgroundColor: '#2c3e50',
              border: '1px solid #555',
              borderRadius: '4px',
              color: 'white',
              cursor: enabled ? 'pointer' : 'not-allowed',
              fontSize: '12px'
            }}
          >
            +100 →
          </button>
        </div>
      </div>

      {/* Info */}
      <div style={{
        padding: '10px',
        backgroundColor: '#1a252f',
        borderRadius: '6px',
        fontSize: '12px',
        color: '#999',
        textAlign: 'center'
      }}>
        Map Width: <strong>{rightBoundary - leftBoundary}px</strong> | 
        Left: <strong>{leftBoundary}px</strong> | 
        Right: <strong>{rightBoundary}px</strong>
      </div>

      {/* Close Button */}
      <button
        onClick={onClose}
        style={{
          width: '100%',
          marginTop: '15px',
          padding: '10px',
          backgroundColor: '#34495e',
          border: 'none',
          borderRadius: '6px',
          color: 'white',
          cursor: 'pointer',
          fontWeight: 'bold'
        }}
      >
        Close
      </button>
    </div>
  );
};
